import { DRAWER_BODY_RESET } from './ui/drawerOverrides';
import React, { useState, useEffect, useMemo } from 'react';
import { Drawer, SIZE, ANCHOR } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Select, TYPE as SELECT_TYPE } from 'baseui/select';
import { Checkbox } from 'baseui/checkbox';
import { styled } from 'baseui';
import { HeadingSmall, LabelSmall, ParagraphSmall } from 'baseui/typography';
import { Tag, Trash2, Save, X } from 'lucide-react';
import { useCreateInvitationType, useUpdateInvitationType, useDeleteInvitationType } from '../hooks/useInvitationTypes';
import type { InvitationType } from '../api/generated';
import { useGates, useGateTypes } from '../hooks/useGates';
import { gateRuleOptions as buildGateRuleOptions, selectedGateRule, toGateRows, type GateKind } from '../pages/settings/gates/gatesModel';
import { useUserPermissions } from '../hooks/useUserPermissions';

// SAM-256 — Residential / Business scope for Invitation Types.
//
// The backend `invitation_type` table has no column to express which
// app(s) a type applies to, and there is no shared table between
// `invitation_type` (Residential) and `business_access_type` (Business) —
// they are two separate lists with separate ids (see
// `src/pages/invitation/components/AccessTypeControl.tsx`). That backend
// work is tracked as SAM-269.
//
// Until SAM-269 lands, this selection is UI-only: it is not persisted
// anywhere (not localStorage, not the API) and does not affect what the
// apps or the gatekeeper see. Keeping a client-side store here would just
// create state to migrate later.
type InvitationTypeScope = {
  residential: boolean;
  business: boolean;
};

const DEFAULT_SCOPE: InvitationTypeScope = { residential: true, business: true };

const DrawerContent = styled('div', {
  padding: '0',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const DrawerHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '24px 24px 20px',
  borderBottom: '1px solid #F3F4F6',
});

const AvatarCircle = styled('div', {
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  backgroundColor: 'var(--tenant-primary, #111827)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

const DrawerBody = styled('div', {
  flex: 1,
  overflowY: 'auto',
  padding: '20px 24px',
});

const Section = styled('div', {
  marginBottom: '24px',
});

const SectionTitle = styled('div', {
  fontSize: '12px',
  fontWeight: 600,
  color: '#9CA3AF',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  marginBottom: '12px',
});

const DrawerFooter = styled('div', {
  padding: '16px 24px',
  borderTop: '1px solid #F3F4F6',
  display: 'flex',
  gap: '12px',
  justifyContent: 'space-between',
});

const ActionBtn = styled('button', {
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#F9FAFB',
  color: '#374151',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: '#F3F4F6' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

const DangerActionBtn = styled('button', {
  padding: '10px 16px',
  borderRadius: '8px',
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  color: '#DC2626',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: '#FEF2F2' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

const PrimaryBtn = styled('button', {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--tenant-primary, #111827)',
  color: '#fff',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.15s ease',
  ':hover': { backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' },
  ':disabled': { opacity: 0.4, cursor: 'not-allowed' },
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invitationType: InvitationType | null;
  mode: 'create' | 'edit';
}

const EMPTY_FORM = {
  name: '',
  duration_limit_days: '',
  max_adults: '',
  max_children: '',
  max_guests_per_day: '',
  external_gate_rules: '',
  internal_gate_rules: '',
};

// The drawer sits at z-index 9999 / 10000 and BaseUI sets its own z-index on the popover, so the dropdown opened
// BEHIND the drawer (QA could not open it). `999999 !important` is the project's rule for a Select inside a drawer
// (EstatesExplorer, ProductSelectControl, BusinessSelectionModal).
const GATE_RULE_SELECT_OVERRIDES = {
  Popover: { props: { overrides: { Body: { style: { zIndex: '999999 !important' } } } } },
  Dropdown: { style: { zIndex: '999999 !important' } },
};

export const InvitationTypeDetailDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  invitationType,
  mode,
}) => {
  const createMutation = useCreateInvitationType();
  const updateMutation = useUpdateInvitationType();
  const deleteMutation = useDeleteInvitationType();

  // SAM-946 (Rodrigo, 15 Oct 2026): gate rules are for the tenant admin and Xhinola only (isAdmin = admin or
  // xhinola_admin). Anyone else with access to this screen (business_roles) does not see them and never sends them;
  // the server must refuse them too.
  const { isAdmin: canEditGateRules } = useUserPermissions();
  const [form, setForm] = useState(EMPTY_FORM);
  const [scope, setLocalScope] = useState<InvitationTypeScope>(DEFAULT_SCOPE);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && invitationType) {
      setForm({
        name: invitationType.name || '',
        duration_limit_days: invitationType.duration_limit_days != null ? String(invitationType.duration_limit_days) : '',
        max_adults: invitationType.max_adults != null ? String(invitationType.max_adults) : '',
        max_children: invitationType.max_children != null ? String(invitationType.max_children) : '',
        max_guests_per_day: invitationType.max_guests_per_day != null ? String(invitationType.max_guests_per_day) : '',
        external_gate_rules: invitationType.external_gate_rules || '',
        internal_gate_rules: invitationType.internal_gate_rules || '',
      });
      // SAM-269: the backend does not persist scope yet, so there is
      // nothing to read back per invitation type — always start from the
      // default (both checked).
      setLocalScope(DEFAULT_SCOPE);
    } else {
      setForm(EMPTY_FORM);
      setLocalScope(DEFAULT_SCOPE);
    }
    setShowDeleteConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationType, mode, isOpen]);

  const isScopeValid = scope.residential || scope.business;

  const toggleScope = (key: keyof InvitationTypeScope) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setLocalScope((prev) => ({ ...prev, [key]: e.target.checked }));

  const setField = (key: keyof typeof EMPTY_FORM) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const setGateRuleField = (key: 'external_gate_rules' | 'internal_gate_rules') => (value: string) =>
    setForm((p) => ({ ...p, [key]: value }));

  // SAM-946: the options are the tenant's real gates (GET /crud/gate), not a fixed list. The rule stores the gate
  // name; a stored value that is not a gate stays visible as a legacy value instead of showing a blank select.
  const { data: gates = [], isLoading: loadingGates } = useGates();
  const { data: gateTypes = [] } = useGateTypes();
  const gateRows = useMemo(() => toGateRows(gates, gateTypes), [gates, gateTypes]);
  const gateRuleOptions = (kind: GateKind, currentValue: string) => buildGateRuleOptions(kind, gateRows, currentValue);
  const gateRuleValue = (kind: GateKind, currentValue: string) => selectedGateRule(gateRuleOptions(kind, currentValue), currentValue);

  const toPayload = (): Partial<InvitationType> => ({
    name: form.name.trim(),
    duration_limit_days: form.duration_limit_days !== '' ? Number(form.duration_limit_days) : undefined,
    max_adults: form.max_adults !== '' ? Number(form.max_adults) : undefined,
    max_children: form.max_children !== '' ? Number(form.max_children) : undefined,
    max_guests_per_day: form.max_guests_per_day !== '' ? Number(form.max_guests_per_day) : undefined,
    ...(canEditGateRules
      ? {
          external_gate_rules: form.external_gate_rules.trim() || undefined,
          internal_gate_rules: form.internal_gate_rules.trim() || undefined,
        }
      : {}),
  });

  const handleSave = () => {
    if (!isScopeValid) return;
    const data = toPayload();
    // SAM-269: `scope` is intentionally not sent to the API and not
    // persisted anywhere — see the note above `InvitationTypeScope`.
    if (mode === 'create') {
      createMutation.mutate(data, {
        onSuccess: () => {
          onClose();
        },
      });
    } else if (invitationType?.id != null) {
      const id = invitationType.id;
      updateMutation.mutate(
        { id, data },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    }
  };

  const handleDelete = () => {
    if (!invitationType?.id) return;
    deleteMutation.mutate(invitationType.id, { onSuccess: () => onClose() });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const isFormValid = form.name.trim().length > 0 && isScopeValid;
  const title = mode === 'create' ? 'New Invitation Type' : invitationType?.name || 'Invitation Type';

  return (
    <Drawer
      isOpen={isOpen}
      autoFocus
      onClose={onClose}
      size={SIZE.default}
      anchor={ANCHOR.right}
      overrides={{
        Root: { style: { zIndex: 9999 } },
        DrawerContainer: { style: { width: '480px', zIndex: 10000 } },
        DrawerBody: { style: { ...DRAWER_BODY_RESET } },
        Close: { style: { display: 'none' } },
      }}
    >
      <DrawerContent>
        <DrawerHeader>
          <AvatarCircle>
            <Tag size={20} color="#fff" />
          </AvatarCircle>
          <div>
            <HeadingSmall margin={0} style={{ fontSize: '18px', fontWeight: 600 }}>{title}</HeadingSmall>
            <LabelSmall color="#6B7280" marginTop="2px">
              {mode === 'create' ? 'Create a new invitation type' : `ID: ${invitationType?.id}`}
            </LabelSmall>
          </div>
        </DrawerHeader>

        <DrawerBody>
          <Section>
            <SectionTitle>Basic Info</SectionTitle>

            <FormControl label="Name *">
              <Input
                value={form.name}
                onChange={setField('name')}
                placeholder="e.g. Guest Visit, Contractor"
                size="compact"
              />
            </FormControl>

            <FormControl label="Duration limit (days)">
              <Input
                value={form.duration_limit_days}
                onChange={setField('duration_limit_days')}
                placeholder="Leave blank for unlimited"
                type="number"
                min="0"
                size="compact"
              />
            </FormControl>
          </Section>

          <Section>
            <SectionTitle>Applies To *</SectionTitle>

            <FormControl
              label=""
              error={!isScopeValid ? 'Select at least one option' : ''}
              caption="Backend support pending (SAM-269) — this selection is not saved yet."
            >
              <div>
                <Checkbox checked={scope.residential} onChange={toggleScope('residential')}>
                  Residential
                </Checkbox>
                <Checkbox checked={scope.business} onChange={toggleScope('business')}>
                  Business
                </Checkbox>
              </div>
            </FormControl>
          </Section>

          <Section>
            <SectionTitle>Guest Limits</SectionTitle>

            <FormControl label="Max adults">
              <Input
                value={form.max_adults}
                onChange={setField('max_adults')}
                placeholder="Leave blank for unlimited"
                type="number"
                min="0"
                size="compact"
              />
            </FormControl>

            <FormControl label="Max children">
              <Input
                value={form.max_children}
                onChange={setField('max_children')}
                placeholder="Leave blank for unlimited"
                type="number"
                min="0"
                size="compact"
              />
            </FormControl>

            <FormControl label="Max guests per day">
              <Input
                value={form.max_guests_per_day}
                onChange={setField('max_guests_per_day')}
                placeholder="Leave blank for unlimited"
                type="number"
                min="0"
                size="compact"
              />
            </FormControl>
          </Section>

          {canEditGateRules && (
          <Section>
            <SectionTitle>Gate Rules</SectionTitle>

            <FormControl label="External gate rules">
              <Select
                type={SELECT_TYPE.select}
                options={gateRuleOptions('external', form.external_gate_rules)}
                value={gateRuleValue('external', form.external_gate_rules)}
                onChange={(params) => setGateRuleField('external_gate_rules')((params.value[0]?.id as string) || '')}
                placeholder={loadingGates ? 'Loading gates…' : 'Select a gate rule'}
                size="compact"
                clearable
                overrides={GATE_RULE_SELECT_OVERRIDES}
              />
            </FormControl>

            <FormControl label="Internal gate rules">
              <Select
                type={SELECT_TYPE.select}
                options={gateRuleOptions('internal', form.internal_gate_rules)}
                value={gateRuleValue('internal', form.internal_gate_rules)}
                onChange={(params) => setGateRuleField('internal_gate_rules')((params.value[0]?.id as string) || '')}
                placeholder={loadingGates ? 'Loading gates…' : 'Select a gate rule'}
                size="compact"
                clearable
                overrides={GATE_RULE_SELECT_OVERRIDES}
              />
            </FormControl>
          </Section>
          )}

          {mode === 'edit' && invitationType && (
            <Section>
              <SectionTitle>Danger Zone</SectionTitle>
              {!showDeleteConfirm ? (
                <DangerActionBtn
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  style={{ width: '100%' }}
                >
                  <Trash2 size={14} />
                  Delete Invitation Type
                </DangerActionBtn>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  <ParagraphSmall margin="0 0 12px" color="#374151" style={{ fontSize: '13px', fontWeight: 500 }}>
                    This action is permanent and cannot be undone.
                  </ParagraphSmall>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <ActionBtn onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting} style={{ flex: 1 }}>
                      Cancel
                    </ActionBtn>
                    <DangerActionBtn onClick={handleDelete} disabled={isDeleting} style={{ flex: 1 }}>
                      <Trash2 size={14} />
                      {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </DangerActionBtn>
                  </div>
                </div>
              )}
            </Section>
          )}
        </DrawerBody>

        <DrawerFooter>
          <ActionBtn onClick={onClose} disabled={isSaving}>
            <X size={14} />
            Cancel
          </ActionBtn>
          <PrimaryBtn onClick={handleSave} disabled={!isFormValid || isSaving}>
            <Save size={14} />
            {isSaving ? 'Saving...' : mode === 'create' ? 'Create Type' : 'Save Changes'}
          </PrimaryBtn>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
